import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const getPortfolioImages = () => ({
  retratos: [
    { id: 1, image: '/imagenes/tatuajes/retrato-1-antebrazo.jpg', key: 'retrato1' },
    { id: 5, image: '/imagenes/tatuajes/retrato-2-facial.jpg', key: 'retrato2' },
    { id: 13, image: '/imagenes/tatuajes/retrato-jesus.jpg', key: 'retratoJesus' },
    { id: 14, image: '/imagenes/tatuajes/retrato-hombre.jpg', key: 'retratoHombre' },
    { id: 15, image: '/imagenes/tatuajes/retrato-virgen.jpg', key: 'retratoVirgen' }
  ],
  animales: [
    { id: 2, image: '/imagenes/tatuajes/animal-1-tigre.jpg', key: 'animal1' },
    { id: 6, image: '/imagenes/tatuajes/animal-2-lobo.png', key: 'animal2' },
    { id: 16, image: '/imagenes/tatuajes/animal-dragon.jpg', key: 'animalDragon' },
    { id: 17, image: '/imagenes/tatuajes/animal-tigre-2.jpg', key: 'animalTiger2' },
    { id: 18, image: '/imagenes/tatuajes/animal-cisne.jpg', key: 'animalSwan' }
  ],
  mitologicos: [
    { id: 3, image: '/imagenes/tatuajes/mitologia-1-zeus.jpg', key: 'mito1' },
    { id: 7, image: '/imagenes/tatuajes/mitologia-2-poseidon.jpg', key: 'mito2' },
    { id: 8, image: '/imagenes/tatuajes/mitologia-3-escultura.jpg', key: 'mito3' },
    { id: 9, image: '/imagenes/tatuajes/mitologia-4-dios.jpg', key: 'mito4' },
    { id: 10, image: '/imagenes/tatuajes/mitologia-5-detalle.jpg', key: 'mito5' },
    { id: 11, image: '/imagenes/tatuajes/mitologia-6-composicion.jpg', key: 'mito6' },
    { id: 19, image: '/imagenes/tatuajes/mitologia-casco.jpg', key: 'mitoHelmet' },
    { id: 20, image: '/imagenes/tatuajes/mitologia-composicion.jpg', key: 'mitoComposition' },
    { id: 21, image: '/imagenes/tatuajes/mitologia-moneda.jpg', key: 'mitoCoin' },
    { id: 22, image: '/imagenes/tatuajes/mitologia-angel.jpg', key: 'mitoAngel' },
    { id: 23, image: '/imagenes/tatuajes/mitologia-caballero.jpg', key: 'mitoKnight' },
    { id: 24, image: '/imagenes/tatuajes/mitologia-terminado.jpg', key: 'mitoGod' },
    { id: 25, image: '/imagenes/tatuajes/mitologia-brazo.jpg', key: 'mitoPortrait' }
  ],
  blackwork: [
    { id: 4, image: '/imagenes/tatuajes/blackwork-1-geometrico.jpg', key: 'black1' },
    { id: 12, image: '/imagenes/tatuajes/blackwork-2-sombras.jpg', key: 'black2' }
  ],
  otros: [
    { id: 26, image: '/imagenes/tatuajes/otros-espacio.jpg', key: 'otherSpace' }
  ]
});

export const Portfolio = () => {
  const { t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('retratos');
  const [selectedImage, setSelectedImage] = useState(null);

  const portfolioImages = getPortfolioImages();
  
  const categories = [
    { id: 'retratos', label: t.portfolio.categories.retratos },
    { id: 'animales', label: t.portfolio.categories.animales },
    { id: 'mitologicos', label: t.portfolio.categories.mitologicos },
    { id: 'blackwork', label: t.portfolio.categories.blackwork },
    { id: 'otros', label: t.portfolio.categories.otros }
  ];

  const getItemData = (key) => t.portfolio.items[key];

  return (
    <section id="portfolio" className="py-20 lg:py-32 bg-[#0B0B0B]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-[#F5F5F5] tracking-tight mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="portfolio-title"
          >
            {t.portfolio.title}
          </h2>
          <p className="text-base sm:text-lg text-[#9CA3AF] max-w-2xl mx-auto">
            {t.portfolio.subtitle}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((category) => (
            <motion.button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-6 py-2.5 text-sm tracking-wide font-medium transition-all ${
                activeCategory === category.id
                  ? 'bg-[#E5E5E5] text-[#0B0B0B]'
                  : 'bg-[#1C1C1C] text-[#9CA3AF] hover:bg-[#2C2C2C] hover:text-[#F5F5F5]'
              }`}
              aria-pressed={activeCategory === category.id}
              data-testid={`portfolio-tab-${category.id}`}
            >
              {category.label}
            </motion.button>
          ))}
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {portfolioImages[activeCategory].map((item, index) => {
              const itemData = getItemData(item.key);
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setSelectedImage({ ...item, ...itemData })}
                  className="relative group cursor-pointer overflow-hidden bg-[#1C1C1C] aspect-square"
                  data-testid={`portfolio-item-${item.id}`}
                >
                  <img
                    src={item.image}
                    alt={itemData.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                    <h3 className="text-[#F5F5F5] text-lg font-medium tracking-wide">
                      {itemData.title}
                    </h3>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0B0B0B]/95 backdrop-blur-sm"
            onClick={() => setSelectedImage(null)}
            data-testid="portfolio-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-4xl w-full bg-[#1C1C1C] overflow-hidden"
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 z-10 text-[#F5F5F5] hover:text-[#FFFFFF] bg-[#0B0B0B]/50 p-2 rounded-full backdrop-blur-sm"
                data-testid="modal-close-button"
              >
                <X size={24} />
              </button>

              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="aspect-square lg:aspect-auto">
                  <img
                    src={selectedImage.image}
                    alt={selectedImage.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="p-8 flex flex-col justify-center">
                  <h3
                    className="text-3xl font-light text-[#F5F5F5] mb-4 tracking-tight"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {selectedImage.title}
                  </h3>
                  <p className="text-base text-[#9CA3AF] mb-6 leading-relaxed">
                    {selectedImage.description}
                  </p>
                  {(selectedImage.size || selectedImage.time) && <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-3">
                      {selectedImage.size && <><span className="text-[#E5E5E5] font-medium">{t.portfolio.labels.size}</span>
                      <span className="text-[#9CA3AF]">{selectedImage.size}</span></>}
                    </div>
                    <div className="flex items-center gap-3">
                      {selectedImage.time && <><span className="text-[#E5E5E5] font-medium">{t.portfolio.labels.time}</span>
                      <span className="text-[#9CA3AF]">{selectedImage.time}</span></>}
                    </div>
                  </div>}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
